<template>
  <NavbarTopMobile
    :label="left_label"
    :avatar_src="left_avatar_src"
    :avatar_format="left_avatar_format"
    data_test_label="partner-mobile-project"
    @open-sidebar="is_menu_open = true"
  >
    <template #pill>
      <a
        v-if="active_pill && active_pill.is_dropdown"
        class="flex min-h-11 min-w-0 cursor-pointer items-center gap-1"
        :data-test="active_pill.data_test"
        @click="toggleFeatureMenu"
      >
        <span class="truncate text-base font-semibold text-gray-800">
          {{ active_pill.label }}
        </span>
        <AppIcon
          name="regular-chevron-down"
          class="shrink-0 text-gray-700"
        />
      </a>
      <span
        v-else-if="active_pill"
        class="truncate text-base font-semibold text-gray-800"
        :data-test="active_pill.data_test"
      >
        {{ active_pill.label }}
      </span>
    </template>

    <template #menus>
      <PMenu
        ref="feature_menu_ref"
        :model="navbar_items"
        :popup="true"
      >
        <template #item="{ item, props }">
          <NavbarItemHorizontal
            :item="item"
            :params="params"
            v-bind="props.action"
          />
        </template>
      </PMenu>
    </template>
  </NavbarTopMobile>
  <TheSidebarPartner
    v-model="is_menu_open"
    @close="is_menu_open = false"
  />
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import TheSidebarPartner from '@/app/_partners/components/navbar/TheSidebarPartner.vue';
import NavbarItemHorizontal from '@/app/shared_components/navbar/NavbarItemHorizontal.vue';
import NavbarTopMobile from '@/app/shared_components/navbar/NavbarTopMobile.vue';
import {
  NAVBAR_ITEMS_PARTNERS_PROJECT,
  NAVBAR_ITEMS_PARTNERS_PROJECT_ACTIVE_ROLE_NEEDED,
} from '@/app/data/navbar_constants';
import { ROUTES_PARTNERS } from '@/app/data/route_constants';

const { proxy: app } = getCurrentInstance();
const store = useStore();
const route = useRoute();
const { t } = useI18n();

const current_tenant = computed(() => store.getters.current_tenant);
const current_project = computed(() => store.getters.current_project);
const current_profile = computed(() => store.getters.current_profile);

const is_menu_open = ref(false);
const feature_menu_ref = ref(null);

const left_label = computed(() => {
  if (current_project.value && current_project.value.attributes && current_project.value.attributes.name) {
    return current_project.value.attributes.name;
  }
  return '';
});

const left_avatar_src = computed(() => {
  if (
    current_project.value &&
    current_project.value.attributes &&
    current_project.value.attributes.picture_login_url_s
  ) {
    return current_project.value.attributes.picture_login_url_s;
  }
  if (current_tenant.value && current_tenant.value.attributes) {
    return current_tenant.value.attributes.picture_logo_url_s;
  }
  return undefined;
});

const left_avatar_format = computed(() => {
  if (current_project.value) return 'square';
  if (current_tenant.value && current_tenant.value.attributes) {
    return current_tenant.value.attributes.picture_logo_format;
  }
  return undefined;
});

const isFlipperEnabled = (item) => !item.flipper || app.$flipper.enabled(item.flipper);

function isRouteActive(r) {
  if (!isFlipperEnabled(r)) return false;
  if (NAVBAR_ITEMS_PARTNERS_PROJECT_ACTIVE_ROLE_NEEDED.includes(r.name)) {
    if (!current_project.value) return false;
    return current_profile.value.attributes.active_role_project_ids.includes(parseInt(current_project.value.id, 10));
  }
  return true;
}

const navbar_items = computed(() => {
  if (!current_project.value) return [];
  return NAVBAR_ITEMS_PARTNERS_PROJECT.filter(isRouteActive);
});

const active_feature = computed(() =>
  navbar_items.value.find((item) => route.name && route.name.startsWith(item.prefix)),
);

const TOP_LEVEL_FEATURES = [
  {
    prefix: ROUTES_PARTNERS.CLIENT_DASHBOARD,
    title: 'navbar.partner.dashboard',
    data_test: 'partner-mobile-dashboard',
  },
  {
    prefix: ROUTES_PARTNERS.OVERVIEW_ALL_PROJECTS,
    title: 'navbar.partner.projects',
    data_test: 'partner-mobile-projects',
  },
  { prefix: ROUTES_PARTNERS.SETTINGS_PROFILE, title: 'navbar.partner.profile', data_test: 'partner-mobile-profile' },
];

const active_top_level_feature = computed(() =>
  TOP_LEVEL_FEATURES.find((item) => route.name && route.name.startsWith(item.prefix)),
);

const active_pill = computed(() => {
  if (active_feature.value) {
    return { label: t(active_feature.value.title), data_test: 'partner-mobile-feature', is_dropdown: true };
  }
  if (active_top_level_feature.value) {
    return {
      label: t(active_top_level_feature.value.title),
      data_test: active_top_level_feature.value.data_test,
      is_dropdown: false,
    };
  }
  return null;
});

const params = computed(() => {
  if (!current_project.value) return {};
  return { project_id: current_project.value.id };
});

const toggleFeatureMenu = (event) => {
  if (feature_menu_ref.value) feature_menu_ref.value.toggle(event);
};
</script>
